import React from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { colors, text } from '../../core/styles/theme/default';
import { H4, Link, P4 } from '../../primitives';
import { Spacer } from '../../helpers';
import months from '../../core/i18n/months';

const StyledEvent = styled.article`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border: ${rem(1)} solid ${colors.grue300};
  border-radius: ${rem(2)};
  padding: ${rem(32)};
  min-height: ${rem(300)};
  cursor: pointer;
  height: 100%;

  ${({ theme }) => theme?.stylesExtension?.event?.event}
`;

const StyledDate = styled.time`
  display: block;
  color: ${colors.nightBlue700};
  ${text.p2};
  font-weight: 700;

  ${({ theme }) => theme?.stylesExtension?.event?.date}
`;

const StyledProvider = styled(P4)`
  color: ${colors.nightBlue700};
  font-weight: 400;

  ${({ theme }) => theme?.stylesExtension?.event?.provider}
`;

const StyledTitle = styled(H4)`
  font-size: ${rem(20)};
  line-height: 1.5;
  overflow-wrap: break-word;

  ${({ theme }) => theme?.stylesExtension?.event?.title}
`;

const StyledTeaser = styled(P4)`
  color: ${colors.grey700};
  font-weight: 400;

  ${({ theme }) => theme?.stylesExtension?.event?.teaser}
`;

const StyledLocation = styled(P4)`
  color: ${colors.nightBlue500};
  font-weight: 400;

  ${({ theme }) => theme?.stylesExtension?.event?.location}
`;

const StyledLink = styled(Link)`
  padding: ${rem(10)} 0;
  margin-bottom: ${rem(-10)};
  margin-top: auto;
  color: ${colors.nightBlue500};

  .m-richtext & {
    text-decoration: none;
  }

  ${({ theme }) => theme?.stylesExtension?.event?.link}
`;

const Event = (props: EventProps) => {
  const {
    node_locale,
    provider,
    title,
    teaser,
    location,
    plz,
    language,
    eventDate,
    href,
    moreLinkText,
  } = props.data;
  const eventDateArr = eventDate.split('-');
  const eventDay = parseInt(eventDateArr[2], 10);
  const eventMonth = parseInt(eventDateArr[1], 10);
  const eventYear = parseInt(eventDateArr[0], 10);
  const address = [plz, location, language].filter(Boolean).join(' | ');

  return (
    <StyledEvent
      className="animate"
      onClick={(e) => {
        if (!(e.target as HTMLElement).closest('a')) {
          e.currentTarget.querySelector('a')?.click();
        }
      }}
    >
      <StyledDate dateTime={eventDate}>
        {eventDay}
        {node_locale === 'de' && '.'} {months.long[node_locale][eventMonth - 1]}{' '}
        {eventYear}
      </StyledDate>
      <Spacer space="gamma" />
      <StyledProvider>{provider}</StyledProvider>
      <Spacer space="alpha" />
      <StyledTitle>{title}</StyledTitle>
      {teaser && (
        <>
          <Spacer space="alpha" />
          <StyledTeaser>{teaser}</StyledTeaser>
        </>
      )}
      {address && (
        <>
          <Spacer space="alpha" />
          <StyledLocation>{address}</StyledLocation>
        </>
      )}
      <Spacer space="gamma" />
      <StyledLink
        linkText={moreLinkText}
        link={{
          href,
          title,
        }}
        variant="internal"
      />
    </StyledEvent>
  );
};

export type EventProps = {
  data: {
    __typename: string;
    node_locale: string;
    provider: string;
    title: string;
    teaser?: string;
    location?: string;
    language?: string;
    plz?: string;
    eventDate: string;
    href: string;
    moreLinkText: string;
  };
};

export default Event;
